import React from 'react';
import { ChevronDown } from 'lucide-react';
import { C } from '../styles';
import type { Section, Employee } from '../types';

interface Props {
  section: Section;
  isOpen: boolean;
  onToggleOpen: () => void;
  employee: Employee;
  onEditClick: () => void;
}

export default function BasePaySectionCard({ section, isOpen, onToggleOpen, employee, onEditClick }: Props) {
  const anyEmp = employee as any;
  const payType = (anyEmp.payType || anyEmp.pay_type || 'hourly').toLowerCase();

  const displayRate =
    payType === 'hourly'
      ? (anyEmp.hourly_rate ? '$' + Number(anyEmp.hourly_rate).toFixed(2) + '/hr' : '-')
      : payType === 'salary'
        ? (anyEmp.salary_amount ? '$' + Number(anyEmp.salary_amount).toLocaleString('en-CA', { minimumFractionDigits: 2 }) + '/year' : '-')
        : 'Commission only';
  const displayFreq = anyEmp.pay_frequency || '-';
  const displayHours = anyEmp.hours_per_day || '-';
  const displayDays = anyEmp.days_per_week || '-';
  const isEmpty =
    payType === 'hourly' ? !anyEmp.hourly_rate
    : payType === 'salary' ? !anyEmp.salary_amount
    : false;
  const pillStyle = isEmpty
    ? { bg: C.amberSoft, fg: C.amber, label: 'Start' }
    : { bg: C.greenSoft, fg: C.green, label: 'Done' };

  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid ' + C.line,
      borderRadius: 12,
      overflow: 'hidden',
      marginBottom: 12,
    }}>
      <div
        onClick={onToggleOpen}
        style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '18px 22px', cursor: 'pointer' }}
      >
        <h3 style={{ flex: 1, fontSize: 16, fontWeight: 700, color: C.ink }}>{section.title}</h3>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 11.5, fontWeight: 600,
          padding: '3px 10px', borderRadius: 20,
          background: pillStyle.bg, color: pillStyle.fg,
        }}>
          {pillStyle.label}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onEditClick(); }}
          style={{
            fontSize: 13.5, fontWeight: 600, color: C.tealInk,
            background: 'none', border: 0, cursor: 'pointer',
            padding: '6px 8px', borderRadius: 8,
          }}
        >
          {pillStyle.label === 'Start' ? 'Start' : 'Edit'}
        </button>
        <ChevronDown
          size={18}
          color={C.muted}
          style={{ transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
        />
      </div>
      {isOpen && (
        <div style={{ padding: '4px 22px 22px', borderTop: '1px solid ' + C.lineSoft }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 0', borderBottom: '1px solid ' + C.lineSoft }}>
            <span style={{ fontSize: 13.5, color: '#12262B', fontWeight: 700 }}>Compensation type</span>
            <span style={{ fontSize: 13.5, color: C.ink, fontWeight: 500 }}>
              {payType === 'hourly' ? 'Hourly' : payType === 'salary' ? 'Salary' : 'Commission only'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 0', borderBottom: '1px solid ' + C.lineSoft }}>
            <span style={{ fontSize: 13.5, color: '#12262B', fontWeight: 700 }}>Rate</span>
            <span style={{ fontSize: 13.5, color: C.ink, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{displayRate}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 0', borderBottom: '1px solid ' + C.lineSoft }}>
            <span style={{ fontSize: 13.5, color: '#12262B', fontWeight: 700 }}>Pay frequency</span>
            <span style={{ fontSize: 13.5, color: C.ink, fontWeight: 500, textTransform: 'capitalize' }}>{displayFreq}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 0', borderBottom: '1px solid ' + C.lineSoft }}>
            <span style={{ fontSize: 13.5, color: '#12262B', fontWeight: 700 }}>Hours per day</span>
            <span style={{ fontSize: 13.5, color: C.ink, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{displayHours}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 0' }}>
            <span style={{ fontSize: 13.5, color: '#12262B', fontWeight: 700 }}>Days per week</span>
            <span style={{ fontSize: 13.5, color: C.ink, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{displayDays}</span>
          </div>
        </div>
      )}
    </div>
  );
}